import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import authReducer, { authInitialState } from "@app-redux/slices/authSlice";
import playlistsPageReducer from "@app-redux/slices/playlistSlice";
import tagFactoryPageReducer, {
  initialTagFactoryState,
} from "@app-redux/slices/tagFactorySlice";
import moodFactoryPageReducer, {
  moodFactoryInitialState,
} from "@app-redux/slices/moodFactorySlice";
import libraryPageReducer from "@app-redux/slices/librarySlice";
import playlistDetailsReducer, {
  playlistDetailsInitialState,
} from "@app-redux/slices/playlistDetailsSlice";
import trackPlayerPageReducer, {
  trackPlayerInitialState,
} from "@app-redux/slices/trackPlayerSlice";
import scheduleReducer, {
  scheduleInitialState,
} from "@app-redux/slices/scheduleSlice";
import maintenanceReducer, {
  maintenanceSessionInitialState,
} from "@app-redux/slices/maintenanceSessionSlice";

const persistConfig = {
  key: "root",
  storage,
  version: 3,
  // trackPlayer must not persist: isPlaying/trackURL survive in localStorage and
  // AudioWaveform auto-plays on canplay when startPlaying is true (new tab / refresh).
  // Prior blacklist used nested slice keys that do not exist on the root state, so
  // nothing was actually excluded.
  blacklist: ["trackPlayer"],
};

const rootReducer = combineReducers({
  auth: authReducer,
  playlists: playlistsPageReducer,
  tagFactory: tagFactoryPageReducer,
  moodFactory: moodFactoryPageReducer,
  libraryPage: libraryPageReducer,
  playlistDetails: playlistDetailsReducer,
  trackPlayer: trackPlayerPageReducer,
  schedule: scheduleReducer,
  maintenanceSession: maintenanceReducer,
});

const mergeState = <T extends object>(
  persistedState: T | undefined,
  initialState: T,
  nestedKeys: (keyof T)[]
): T => {
  if (!persistedState) return initialState;

  const merged = { ...initialState, ...persistedState };

  for (const key of nestedKeys) {
    if (
      initialState[key] &&
      typeof initialState[key] === "object" &&
      !Array.isArray(initialState[key])
    ) {
      merged[key] = {
        ...(initialState[key] as any),
        ...(persistedState[key] as any),
      };
    }
  }

  return merged;
};
const persistedReducer = persistReducer(
  {
    ...persistConfig,
    migrate: async (state: any) => {
      if (!state) return null;

      return Promise.resolve({
        ...state,
        auth: mergeState(state.auth, authInitialState, []),
        tagFactory: mergeState(state.tagFactory, initialTagFactoryState, []),
        moodFactory: mergeState(state.moodFactory, moodFactoryInitialState, [
          "moodsFilter",
          "tracksTagFilter",
        ]),
        playlistDetails: mergeState(
          state.playlistDetails,
          playlistDetailsInitialState,
          []
        ),
        trackPlayer: trackPlayerInitialState,
        schedule: mergeState(state.schedule, scheduleInitialState, []),
        maintenanceSession: mergeState(
          state.maintenanceSession,
          maintenanceSessionInitialState,
          []
        ),
      });
    },
  },
  rootReducer
);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
